import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import '../../App.css';
import DataTable, { Column } from '../../components/DataTable';
import FilterBar, { ForeignKeyOption } from '../../components/FilterBar';
import { eventTemplateFields, FilterValue, applyFilters } from '../../utils/entityFields';
import { ConfirmModal, SkeletonTable } from '../../components/ui';
import EmptyState from '../../components/ui/EmptyState';
import PageHeader from '../../components/PageHeader';
import { translateError } from '../../utils/errorMessages';

interface IEventTemplate {
  _id: string;
  name: string;
  description: string;
  firstOccurrence: string;
  startTime: string;
  endTime: string;
  recurringPattern: string;
  defaultAttendance: boolean | null;
  isActive: boolean;
  activityId?: number | null;
  courseId?: number | null;
  members: string[];
  groups: string[];
}

interface INamed {
  _id: string | number;
  name: string;
}

const EventTemplatesListPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { hasPermission } = useAuth();
  const { addToast } = useToast();
  const [eventTemplates, setEventTemplates] = useState<IEventTemplate[]>([]);
  const [activities, setActivities] = useState<INamed[]>([]);
  const [courses, setCourses] = useState<INamed[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<FilterValue[]>([]);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Permission checks
  const canCreate = hasPermission('events:write');
  const canEdit = hasPermission('events:write');
  const canDelete = hasPermission('events:delete');

  const fetchEventTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.eventTemplates.list();
      setEventTemplates(data as IEventTemplate[]);
      // Activity/course options power the filter dropdowns (non-critical).
      try {
        const [activitiesRes, coursesRes] = await Promise.all([
          api.activities.list(),
          api.courses.list(),
        ]);
        setActivities((activitiesRes.data as INamed[]).map(a => ({ _id: String(a._id), name: a.name })));
        setCourses((coursesRes.data as INamed[]).map(c => ({ _id: String(c._id), name: c.name })));
      } catch {
        // Ignore — filters degrade gracefully without options.
      }
      setError(null);
    } catch (err: any) {
      setError(translateError(err, t));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEventTemplates();
  }, [fetchEventTemplates]);

  const handleDeleteClick = (template: IEventTemplate) => {
    if (!canDelete) return;
    setDeleteId(template._id);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteId) return;
    setDeleteLoading(true);
    try {
      const { data } = await api.eventTemplates.delete(deleteId);
      addToast(t('templates.deletedAlert', { count: data.eventsDeleted }), 'success');
      // Spared sessions are now orphans of the series — the admin has to know
      // which ones survived and why, or they look simply lost.
      if (data.eventsKept > 0) {
        addToast(
          t('dataSafety.templateEventsKept', {
            count: data.eventsKept,
            cancelled: data.chargesCancelled ?? 0,
          }),
          'warning',
        );
      }
      fetchEventTemplates();
    } catch (err: any) {
      addToast(translateError(err, t, 'templates.deleteError'), 'error');
    } finally {
      setDeleteLoading(false);
      setDeleteId(null);
    }
  };

  // Get recurrence description
  const getRecurrenceDesc = useCallback((template: IEventTemplate): string => {
    const date = new Date(template.firstOccurrence);
    const locale = i18n.language === 'cs' ? 'cs-CZ' : 'en-US';
    const dayName = date.toLocaleDateString(locale, { weekday: 'long' });
    const dayOfMonth = date.getDate();

    switch (template.recurringPattern.toLowerCase()) {
      case 'daily':
        return t('templates.daily');
      case 'weekly':
        return t('templates.weeklyOn', { dayName });
      case 'biweekly':
      case 'bi-weekly':
        return t('templates.biweeklyOn', { dayName });
      case 'monthly':
        return t('templates.monthlyDay', { dayOfMonth });
      default:
        return template.recurringPattern;
    }
  }, [t, i18n.language]);

  const columns: Column<IEventTemplate>[] = useMemo(() => [
    {
      header: t('common.name'),
      sortValue: (template) => template.name ?? null,
      render: (template) => (
        <>
          <strong>{template.name}</strong>
          <br />
          <small style={{ color: 'var(--text-muted)' }}>{template.description}</small>
        </>
      ),
    },
    {
      header: t('templates.schedule'),
      render: (template) => getRecurrenceDesc(template),
    },
    {
      header: t('templates.time'),
      sortValue: (template) => template.startTime ?? null,
      render: (template) => `${template.startTime} - ${template.endTime}`,
    },
    {
      header: t('events.defaultAttendance'),
      render: (template) => (
        <span style={{
          padding: '4px 8px',
          borderRadius: '4px',
          backgroundColor: template.defaultAttendance === true ? 'rgba(40, 167, 69, 0.2)' : template.defaultAttendance === false ? 'rgba(220, 53, 69, 0.2)' : 'rgba(128, 128, 128, 0.2)',
          color: template.defaultAttendance === true ? '#28a745' : template.defaultAttendance === false ? '#dc3545' : '#808080',
          fontSize: '0.85rem',
        }}>
          {template.defaultAttendance === true ? t('common.yes') : template.defaultAttendance === false ? t('common.no') : t('common.maybe')}
        </span>
      ),
    },
    {
      header: t('common.status'),
      sortValue: (template) => (template.isActive ? 1 : 0),
      render: (template) => (
        <span style={{
          padding: '4px 8px',
          borderRadius: '4px',
          backgroundColor: template.isActive ? 'rgba(40, 167, 69, 0.2)' : 'rgba(220, 53, 69, 0.2)',
          color: template.isActive ? '#28a745' : '#dc3545',
          fontSize: '0.85rem',
        }}>
          {template.isActive ? t('common.active') : t('common.inactive')}
        </span>
      ),
    },
  ], [t, getRecurrenceDesc]);

  const filteredTemplates = useMemo(() => applyFilters(eventTemplates, filters), [eventTemplates, filters]);

  const foreignKeyOptions = useMemo<Record<string, ForeignKeyOption[]>>(() => ({
    activities: activities.map(a => ({ value: String(a._id), label: a.name })),
    courses: courses.map(c => ({ value: String(c._id), label: c.name })),
  }), [activities, courses]);

  const handleFilterChange = useCallback((newFilters: FilterValue[]) => {
    setFilters(newFilters);
  }, []);

  if (error) return <p className="error-message">{t('common.error')}: {error}</p>;

  return (
    <div className="admin-list-page">
      <PageHeader
        title={t('templates.title')}
        helpKey="help.admin.eventTemplates"
        action={canCreate ? <Link to="/admin/event-templates/create" className="btn btn--primary btn--md">{t('templates.createNew')}</Link> : undefined}
      />

      <FilterBar fields={eventTemplateFields} onFilterChange={handleFilterChange} foreignKeyOptions={foreignKeyOptions} />

      {loading ? (
        <SkeletonTable rows={5} columns={5} />
      ) : filteredTemplates.length === 0 ? (
        <EmptyState
          title={t('templates.emptyTitle')}
          description={t('templates.emptyDescription')}
          action={canCreate ? { label: t('templates.createNew'), href: '/admin/event-templates/create' } : undefined}
        />
      ) : (
        <DataTable
          data={filteredTemplates}
          columns={columns}
          editLink={canEdit ? (template) => `/admin/event-templates/${template._id}/edit` : undefined}
          onDelete={canDelete ? handleDeleteClick : undefined}
        />
      )}

      <ConfirmModal
        isOpen={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDeleteConfirm}
        title={t('templates.deleteTitle')}
        message={t('templates.confirmDelete')}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        variant="danger"
        loading={deleteLoading}
      />
    </div>
  );
};

export default EventTemplatesListPage;
